import { useState, useEffect } from 'react';
import { useAppStore } from '@/store/appStore';
import { Plus, Trash2, ExternalLink, Copy, Check, Lock, Globe, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function BookmarksView({ projectId }) {
    const { bookmarks, fetchBookmarks, createBookmark, deleteBookmark } = useAppStore();
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        url: '',
        username: '',
        password: ''
    });

    useEffect(() => {
        if (projectId) {
            fetchBookmarks(projectId);
        }
    }, [projectId, fetchBookmarks]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await createBookmark({
                ...formData,
                project_id: projectId
            });
            toast.success('Bookmark added');
            setIsAddOpen(false);
            setFormData({ title: '', url: '', username: '', password: '' });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this bookmark?')) return;
        try {
            await deleteBookmark(id);
            toast.success('Bookmark deleted');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const copyToClipboard = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopiedId(null), 2000);
    };

    const getDomain = (url) => {
        try {
            const domain = new URL(url).hostname;
            return domain.replace('www.', '');
        } catch {
            return 'link';
        }
    };

    const getFavicon = (url) => {
        try {
            const domain = new URL(url).hostname;
            return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
        } catch {
            return null;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-white">Project Bookmarks</h2>
                    <p className="text-zinc-400 text-sm">Important links and credentials</p>
                </div>

                <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-cyan-500 text-black hover:bg-cyan-400">
                            <Plus className="w-4 h-4 mr-2" /> Add Bookmark
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                        <DialogHeader>
                            <DialogTitle className="text-white">Add New Bookmark</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <label className="text-sm text-zinc-400 block mb-1.5">Title</label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. Production DB"
                                    className="bg-zinc-950 border-zinc-800"
                                    required
                                />
                            </div>
                            <div>
                                <label className="text-sm text-zinc-400 block mb-1.5">URL</label>
                                <Input
                                    value={formData.url}
                                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                    placeholder="https://..."
                                    className="bg-zinc-950 border-zinc-800"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-zinc-400 block mb-1.5">Username (Optional)</label>
                                    <Input
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        placeholder="admin"
                                        className="bg-zinc-950 border-zinc-800"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-zinc-400 block mb-1.5">Password (Optional)</label>
                                    <Input
                                        type="text"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder="secret123"
                                        className="bg-zinc-950 border-zinc-800"
                                    />
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-cyan-500 text-black hover:bg-cyan-400"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Saving...' : 'Save Bookmark'}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookmarks.length === 0 ? (
                    <div className="col-span-full text-center py-12 border border-dashed border-zinc-800 rounded-xl">
                        <Globe className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-white">No bookmarks yet</h3>
                        <p className="text-zinc-500">Add important links to get started</p>
                    </div>
                ) : (
                    bookmarks.map((bookmark) => (
                        <Card key={bookmark.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors">
                            <CardHeader className="pb-3">
                                <div className="flex justify-between items-start gap-2">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                                            <img
                                                src={getFavicon(bookmark.url)}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'about:blank';
                                                    e.target.style.display = 'none';
                                                    e.target.parentNode.innerHTML = '<svg class="w-4 h-4 text-zinc-400" ...><path ...></path></svg>'; // Simplified fallback
                                                }}
                                                className="w-4 h-4"
                                                alt=""
                                            />
                                            {/* JS-based fallback if image fails or isn't there initially */}
                                            {!getFavicon(bookmark.url) && <Globe className="w-4 h-4 text-zinc-400" />}
                                        </div>
                                        <div className="min-w-0">
                                            <CardTitle className="text-base font-medium truncate text-white" title={bookmark.title}>
                                                {bookmark.title}
                                            </CardTitle>
                                            <a
                                                href={bookmark.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-zinc-500 hover:text-cyan-400 flex items-center gap-1 truncate"
                                            >
                                                {getDomain(bookmark.url)} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-zinc-500 hover:text-red-400 hover:bg-red-950/20 -mr-2 -mt-2"
                                        onClick={() => handleDelete(bookmark.id)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            {(bookmark.username || bookmark.password) && (
                                <CardContent className="pt-0 pb-4 space-y-2">
                                    <div className="h-px bg-zinc-800 mb-3" />
                                    {bookmark.username && (
                                        <div className="flex items-center justify-between group p-2 rounded-md hover:bg-zinc-800/50 transition-colors">
                                            <div className="flex items-center gap-2 text-zinc-400">
                                                <User className="w-3.5 h-3.5" />
                                                <span className="text-xs font-mono">{bookmark.username}</span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={() => copyToClipboard(bookmark.username, `user-${bookmark.id}`)}
                                            >
                                                {copiedId === `user-${bookmark.id}` ? (
                                                    <Check className="w-3 h-3 text-emerald-400" />
                                                ) : (
                                                    <Copy className="w-3 h-3 text-zinc-400" />
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                    {bookmark.password && (
                                        <div className="flex items-center justify-between group p-2 rounded-md hover:bg-zinc-800/50 transition-colors">
                                            <div className="flex items-center gap-2 text-zinc-400">
                                                <Lock className="w-3.5 h-3.5" />
                                                <span className="text-xs font-mono">••••••••</span>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={() => copyToClipboard(bookmark.password, `pass-${bookmark.id}`)}
                                            >
                                                {copiedId === `pass-${bookmark.id}` ? (
                                                    <Check className="w-3 h-3 text-emerald-400" />
                                                ) : (
                                                    <Copy className="w-3 h-3 text-zinc-400" />
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            )}
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
}
